import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { CheckCircle, XCircle, Clock, ShoppingBag } from 'lucide-react';

// Lands here after M-Pesa (Pesapal), Stripe, PayPal, or Skrill payment.
// For Pesapal specifically, our backend already fetched the real payment
// status from Pesapal's GetTransactionStatus API before redirecting here -
// this page just displays whatever status it was handed via the query string.
const PaymentCallbackPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [status, setStatus] = useState('pending');
  const [orderId, setOrderId] = useState('');

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    setStatus(params.get('status') || 'pending');
    setOrderId(params.get('order_id') || '');
  }, [location]);

  const content = {
    completed: {
      icon: <CheckCircle className="w-16 h-16 text-green-600" />,
      title: 'Payment Successful!',
      description: "Thank you - your order has been confirmed and we're getting it ready.",
    },
    processing: {
      icon: <Clock className="w-16 h-16 text-amber-500" />,
      title: 'Payment Processing',
      description: "We're confirming your payment - this usually only takes a moment. You'll get an update by email shortly.",
    },
    pending: {
      icon: <Clock className="w-16 h-16 text-amber-500" />,
      title: 'Payment Pending',
      description: "We haven't received confirmation of your payment yet. If you completed payment on the previous page, this can take a minute to update.",
    },
    failed: {
      icon: <XCircle className="w-16 h-16 text-red-600" />,
      title: 'Payment Not Completed',
      description: 'Your payment did not go through. No charge was made - please try again.',
    },
    cancelled: {
      icon: <XCircle className="w-16 h-16 text-red-600" />,
      title: 'Payment Cancelled',
      description: 'You cancelled the payment before it was completed.',
    },
  };

  const { icon, title, description } = content[status] || content.pending;

  return (
    <Layout>
      <div className="max-w-md mx-auto px-4 py-24 text-center">
        <Card className="luxury-card">
          <CardContent className="pt-8 pb-8 space-y-4">
            <div className="flex justify-center">{icon}</div>
            <h1 className="text-2xl font-bold">{title}</h1>
            <p className="text-muted-foreground">{description}</p>

            <div className="flex flex-col gap-2 pt-4">
              {orderId && (status === 'completed' || status === 'processing') && (
                <Button onClick={() => navigate('/dashboard')} className="w-full">
                  <ShoppingBag className="w-4 h-4 mr-2" />
                  View My Orders
                </Button>
              )}
              {(status === 'failed' || status === 'cancelled' || status === 'pending') && (
                <Button onClick={() => navigate('/checkout')} className="w-full">
                  Try Again
                </Button>
              )}
              <Link to="/products">
                <Button variant="outline" className="w-full">Continue Shopping</Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default PaymentCallbackPage;
